import { Button } from '@jouzen/ecom-components/v3/Button';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import type { NumberFormatOptions } from 'next-intl';
import { useFormatter, useTranslations } from 'next-intl';

import { EventType } from '@/analytics/types';
import Image from '@/app/components/Image';
import InverseHeaderResponsive from '@/app/components/InverseHeaderResponsive';
import Motion from '@/app/components/Motion';
import RichText from '@/app/components/RichText';
import useCurrency from '@/app/hooks/useCurrency';
import { useLowestRingsPricing } from '@/queries/LowestRingsPricing';

import useRingCardData, { type RingCardData } from './_hooks/useRingCardData';

export interface BasePriceProps {
  readonly showFromPricing: boolean;
  readonly style: RingCardData['style'];
}

const BasePrice = ({ showFromPricing, style }: BasePriceProps) => {
  const currency = useCurrency();
  const format = useFormatter();
  const { data: lowestRingsPricing, isLoading } = useLowestRingsPricing({
    includeDiscount: true,
    ringStyle: style,
  });

  if (!isLoading && lowestRingsPricing?.price?.amount) {
    const formatterOptions: NumberFormatOptions = {
      currency: currency.currencyCode,
      style: 'currency',
      trailingZeroDisplay: 'stripIfInteger',
    };
    const formattedPrice = format.number(
      lowestRingsPricing.price.amount,
      formatterOptions,
    );

    return (
      <Typography className="text-sm md:text-white lg:text-base" weight="bold">
        {showFromPricing ? (
          <RichText
            messageKey="pdp_from"
            tValues={{ amount: formattedPrice }}
          />
        ) : (
          formattedPrice
        )}
      </Typography>
    );
  }

  return (
    <Typography className="text-sm md:text-white lg:text-base" weight="bold">
      &nbsp;
    </Typography>
  );
};

const HomeRingModules = ({
  membershipRingCardData = undefined,
}: {
  readonly membershipRingCardData?: RingCardData[];
}) => {
  const t = useTranslations();
  const ringCardData = useRingCardData();

  const handleClick = async ({
    cta,
    location,
    path,
    action,
  }: {
    cta: string;
    location: string;
    path: string;
    action: string;
  }): Promise<void> => {
    await window.ouraAnalytics.track(EventType.CTAClicked, {
      cta,
      location,
      path,
      action,
    });
  };

  return (
    <InverseHeaderResponsive enterTarget="desktop">
      <section
        className="relative gap-6 px-6 pt-6 md:flex md:flex-row lg:pt-0"
        data-cy="home-ring-module"
      >
        {(membershipRingCardData ? membershipRingCardData : ringCardData).map(
          ({
            dataCyExplore,
            dataCyShop,
            hrefExplore,
            hrefShop,
            includeBgGradient,
            location,
            pdpAction,
            popAction,
            showFromPricing,
            src,
            style,
            title,
          }) => (
            <Motion
              className="flex-1"
              initial={{ opacity: 0 }}
              key={style}
              transition={{ duration: 1, type: 'tween', delay: 0.25 }}
              whileInView={{ opacity: 1 }}
            >
              <div
                className="relative size-full items-end pb-6 md:flex md:pb-0"
                data-cy={title}
              >
                <div className="relative aspect-[10/7] w-full overflow-hidden rounded-xl">
                  <Image fill sizes="(min-width: 768px) 46vw, 95vw" src={src} />
                  {includeBgGradient ? (
                    <div className="absolute bottom-0 h-[45%] w-full bg-linear-to-t from-black/65 to-transparent max-md:hidden" />
                  ) : null}
                </div>
                <div className="flex w-full justify-between pt-4 md:absolute md:px-4 md:pt-0 md:pb-4 lg:px-6 lg:pb-6 xl:px-10 xl:pb-10">
                  <div className="md:self-end">
                    <Typography className="text-2xl md:text-2xl md:font-extralight md:text-white lg:text-3xl xl:pb-[3px] xl:text-4xl xxl:text-5xl">
                      {t(title)}
                    </Typography>
                    <div className="flex text-sandstone-500 md:text-white">
                      <BasePrice
                        showFromPricing={showFromPricing}
                        style={style}
                      />
                    </div>
                  </div>
                  <div className="flex flex-row items-center md:flex-col-reverse md:items-end md:gap-2 lg:gap-4 xl:flex-row xl:items-end">
                    <Button
                      variant={'outlined-light'}
                      size="medium"
                      className="hidden w-full justify-around font-bold text-nowrap hover:text-gray-400 md:flex lg:px-4 lg:py-2 xl:px-6 xl:py-3"
                      href={hrefExplore}
                      data-cy={dataCyExplore}
                      onClick={() =>
                        handleClick({
                          cta: 'explore',
                          location,
                          path: hrefExplore,
                          action: popAction,
                        })
                      }
                    >
                      {t('explore')}
                    </Button>

                    <Button
                      variant="unset"
                      size="medium"
                      className="w-full justify-around bg-sandstone-500 font-bold text-nowrap text-sandstone-200 hover:bg-gray-600 md:border md:border-sandstone-100 md:bg-sandstone-200 md:text-sandstone-500 md:hover:border-gray-400 md:hover:bg-gray-400 lg:px-4 lg:py-2 xl:px-6 xl:py-3"
                      href={hrefShop}
                      data-cy={dataCyShop}
                      onClick={() =>
                        handleClick({
                          cta: 'shop',
                          location,
                          path: hrefShop,
                          action: pdpAction,
                        })
                      }
                    >
                      {t('shop')}
                    </Button>
                  </div>
                </div>
              </div>
            </Motion>
          ),
        )}
      </section>
    </InverseHeaderResponsive>
  );
};
export default HomeRingModules;
